import { Flex } from '@byecode/ui'
import {
    type BackgroundNoImageProtocol,
    type Direction,
    type Fit,
    type ImageInfo,
    type ImageSwiperItem,
    ImageVariableValue,
    type Shape,
    type SizeConfigure
} from '@lighthouse/core'
import { getExplicitHeight, getImageFullUrlInApplication, getWidthSize, useFlowLayoutContext } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import FileItem from './FileItem'

const SCFlex = styled(Flex)<{ $horizontal?: boolean; $overflowX?: string }>`
    width: 100%;
    ${({ $horizontal, $overflowX }) =>
        $horizontal &&
        `
        overflow-x: ${$overflowX ?? 'visible'};
        flex-wrap: nowrap;
    `}
`

interface ListProps {
    noLazy?: boolean
    gridGap?: SizeConfigure
    direction?: Direction
    overflow?: 'hidden' | 'auto' | 'visible'
    value: ImageSwiperItem[]
    isSinglePicture: boolean
    shape: Shape
    aspectRatio: string
    radius?: string
    fitType: Fit
    appId?: string
    background?: BackgroundNoImageProtocol
    width: number
    height: number
    listItemHeight?: SizeConfigure
    compress?: boolean
    isClickEvent?: boolean
    handImageClick: (ev: React.MouseEvent, info: ImageInfo) => void
    hasCustomCursor: boolean
}

export const List = (props: ListProps) => {
    const {
        noLazy = false,
        gridGap,
        direction = 'vertical',
        overflow = 'visible',
        value,
        isSinglePicture,
        shape,
        aspectRatio,
        radius,
        fitType,
        appId,
        background,
        width,
        height,
        listItemHeight,
        compress,
        isClickEvent,
        handImageClick,
        hasCustomCursor
    } = props

    const { sortable } = useFlowLayoutContext()
    const borderRadius = useMemo(() => {
        if (shape === 'round') {
            return '50%'
        }
        return radius ? `${radius}px` : undefined
    }, [radius, shape])

    const gridGapCss = gridGap ? (getWidthSize(gridGap) as { width?: string }).width ?? '0px' : undefined
    const flexDirection = direction === 'horizontal' ? 'row' : 'column'
    const isHorizontal = direction === 'horizontal'

    const imageHeightForCompress = useMemo(() => {
        if (!isHorizontal || !listItemHeight) {
            return
        }
        // 百分比/填充高度依赖布局链，不参与压缩尺寸计算，避免 src 频繁变化造成闪烁
        if (listItemHeight.size === 'auto' || listItemHeight.size === 'fill' || listItemHeight.unit === '%') {
            return undefined
        }
        const explicitHeight = getExplicitHeight(listItemHeight, height, sortable)
        return explicitHeight > 0 ? explicitHeight : undefined
    }, [isHorizontal, listItemHeight, height, sortable])

    const imageOption = useMemo(() => {
        if (isHorizontal) {
            return imageHeightForCompress && imageHeightForCompress > 0 ? { height: imageHeightForCompress, compress } : { compress }
        }
        return width > 0 ? { width, compress } : { compress }
    }, [isHorizontal, width, compress, imageHeightForCompress])

    // const containerStyle = useMemo(() => {
    //     if (isHorizontal) {
    //         // 高度固定，宽度随图片比例撑开
    //         return { height: horizontalImageHeightStyle, width: 'auto', flexShrink: 0 as const }
    //     }
    //     // 宽度固定，高度随图片比例自适应
    //     return { width }
    // }, [isHorizontal, horizontalImageHeightStyle, width])
    const imageStyle = useMemo<React.CSSProperties>(() => {
        if (isSinglePicture) {
            return {
                width: '100%'
            }
        }

        if (!isHorizontal) {
            return {}
        }
        return {
            // 横向列表以高度为基准展示，避免移动端 width:100% 导致的裁剪
            height: imageHeightForCompress || '100%',
            width: 'auto',
            maxWidth: 'none'
        }
    }, [imageHeightForCompress, isHorizontal, isSinglePicture])

    return (
        <SCFlex direction={flexDirection} gap={gridGapCss} $horizontal={isHorizontal} $overflowX={isHorizontal ? overflow : undefined}>
            {value.map((v, i) => {
                if (isSinglePicture && i > 0) {
                    return null
                }

                return (
                    <FileItem
                        {...v}
                        noLazy={noLazy}
                        key={v.url}
                        aspectRatio={isSinglePicture ? aspectRatio : undefined}
                        // containerStyle={containerStyle}
                        src={getImageFullUrlInApplication(appId, v.url, imageOption)}
                        style={{ borderRadius, cursor: !isClickEvent || hasCustomCursor ? undefined : 'pointer', ...imageStyle }}
                        objectFit={fitType}
                        data-stop-action-propagation={isClickEvent ? true : undefined}
                        onClick={ev =>
                            handImageClick(ev, {
                                [ImageVariableValue.IMAGE_INDEX]: i,
                                [ImageVariableValue.IMAGE_URL]: v.url,
                                [ImageVariableValue.IMAGE_NAME]: v.title
                            })
                        }
                    />
                )
            })}
        </SCFlex>
    )
}
